import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Label,
  Textarea,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { hedgesAdminApi, type Hedge } from "@/services/api";
import {
  ArrowLeft,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Building2,
  FileText,
  Calendar,
  DollarSign,
  TrendingUp,
  Shield,
  Download,
  FileCheck,
} from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/utils";

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "success" | "warning" | "destructive"; description: string }> = {
  PENDING_GUARANTEE: { label: "Aguardando Garantia", variant: "warning", description: "A empresa precisa depositar a garantia em USDC" },
  PENDING_APPROVAL: { label: "Aguardando Aprovação", variant: "warning", description: "Hedge aguardando aprovação do administrador" },
  ACTIVE: { label: "Ativo", variant: "success", description: "Proteção ativa com valor garantido" },
  PENDING_SETTLEMENT: { label: "Aguardando Liquidação", variant: "warning", description: "Hedge próximo do vencimento, aguardando liquidação" },
  SETTLED: { label: "Liquidado", variant: "secondary", description: "Hedge liquidado com sucesso" },
  REJECTED: { label: "Rejeitado", variant: "destructive", description: "Hedge rejeitado pelo administrador" },
  DEFAULTED: { label: "Inadimplente", variant: "destructive", description: "Empresa não liquidou no prazo" },
  CANCELLED: { label: "Cancelado", variant: "secondary", description: "Hedge cancelado" },
};

export function HedgeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const action = searchParams.get("action");

  const [hedge, setHedge] = useState<Hedge | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Dialog states
  const [showApproveDialog, setShowApproveDialog] = useState(action === "approve");
  const [showRejectDialog, setShowRejectDialog] = useState(action === "reject");
  const [showDefaultDialog, setShowDefaultDialog] = useState(action === "default");
  const [approveNotes, setApproveNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [defaultNotes, setDefaultNotes] = useState("");
  const [downloadingTerm, setDownloadingTerm] = useState(false);

  useEffect(() => {
    if (id) {
      loadHedge();
    }
  }, [id]);

  async function loadHedge() {
    try {
      setLoading(true);
      const data = await hedgesAdminApi.getById(id!);
      setHedge(data);
    } catch (error) {
      console.error("Erro ao carregar hedge:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove() {
    if (!hedge) return;
    try {
      setActionLoading(true);
      await hedgesAdminApi.approve(hedge.id, approveNotes || undefined);
      setShowApproveDialog(false);
      loadHedge();
    } catch (error) {
      console.error("Erro ao aprovar hedge:", error);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReject() {
    if (!hedge || !rejectReason) return;
    try {
      setActionLoading(true);
      await hedgesAdminApi.reject(hedge.id, rejectReason);
      setShowRejectDialog(false);
      loadHedge();
    } catch (error) {
      console.error("Erro ao rejeitar hedge:", error);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleMarkDefaulted() {
    if (!hedge) return;
    try {
      setActionLoading(true);
      await hedgesAdminApi.markDefaulted(hedge.id, defaultNotes || undefined);
      setShowDefaultDialog(false);
      loadHedge();
    } catch (error) {
      console.error("Erro ao marcar inadimplente:", error);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDownloadTerm() {
    if (!hedge?.termAcceptedAt) return;
    try {
      setDownloadingTerm(true);
      const blob = await hedgesAdminApi.getTermPdf(hedge.id);

      // Criar link de download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `termo-hedge-${hedge.hedgeCode}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar termo:", error);
    } finally {
      setDownloadingTerm(false);
    }
  }

  const formatUsdc = (value: string) => {
    const num = parseFloat(value || "0");
    return num.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    });
  };

  const formatBrl = (value: string) => {
    const num = parseFloat(value || "0");
    return num.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!hedge) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Hedge não encontrado</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/admin/hedges")}>
          Voltar
        </Button>
      </div>
    );
  }

  const status = statusConfig[hedge.status] || { label: hedge.status, variant: "secondary" as const, description: "" };

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Shield className="h-5 w-5 sm:h-6 sm:w-6" />}
        title={`Hedge ${hedge.hedgeCode}`}
        description={status.description}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button className="bg-white/20 hover:bg-white/30 text-white border-0" onClick={() => navigate("/admin/hedges")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            {hedge.status === "PENDING_APPROVAL" && (
              <>
                <Button className="bg-green-500 hover:bg-green-600 text-white border-0" onClick={() => setShowApproveDialog(true)}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Aprovar
                </Button>
                <Button className="bg-red-500 hover:bg-red-600 text-white border-0" onClick={() => setShowRejectDialog(true)}>
                  <XCircle className="w-4 h-4 mr-2" />
                  Rejeitar
                </Button>
              </>
            )}
            {(hedge.status === "ACTIVE" || hedge.status === "PENDING_SETTLEMENT") && (
              <Button className="bg-red-500 hover:bg-red-600 text-white border-0" onClick={() => setShowDefaultDialog(true)}>
                <AlertTriangle className="w-4 h-4 mr-2" />
                Marcar Inadimplente
              </Button>
            )}
          </div>
        }
      />

      {/* Status Badge */}
      <div className="flex items-center gap-4">
        <Badge variant={status.variant} className="text-sm px-3 py-1">
          {status.label}
        </Badge>
        <span className="text-sm text-muted-foreground">
          Criado em {formatDateTime(hedge.createdAt)}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Valores do Hedge */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Valores do Hedge
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-muted-foreground">Valor Final Garantido em BRL</Label>
              <p className="text-2xl font-bold text-primary">{formatBrl(hedge.lockedAmountBrl)}</p>
              <p className="text-sm text-muted-foreground mt-1">
                Baseado em uma taxa de referência de R$ {parseFloat(hedge.lockedExchangeRate).toFixed(4)}
              </p>
            </div>

            <div className="border-t pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <Label className="text-muted-foreground">Valor do Hedge</Label>
                  <p className="font-medium">{formatUsdc(hedge.hedgeAmountUsdc)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Garantia ({hedge.guaranteePercentage}%)</Label>
                  <p className="font-medium">{formatUsdc(hedge.guaranteeAmountUsdc)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Fee de Proteção</Label>
                  <p className="font-medium">{formatBrl(hedge.protectionFeeBrl || hedge.spreadAmountBrl || "0")}</p>
                  {hedge.operationFeeBrl && hedge.capitalCostBrl && (
                    <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                      <p>Taxa operacional: {formatBrl(hedge.operationFeeBrl)}</p>
                      <p>Custo de capital: {formatBrl(hedge.capitalCostBrl)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Taxas de Referência */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Taxas de Referência
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-muted-foreground">Câmbio Spot</Label>
                <p className="font-medium">R$ {parseFloat(hedge.spotExchangeRate).toFixed(4)}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Taxa de Referência (com fee)</Label>
                <p className="font-medium">
                  R$ {parseFloat(hedge.lockedExchangeRate).toFixed(4)}
                </p>
              </div>
            </div>

            {hedge.settlementSpotRate && (
              <div className="border-t pt-4">
                <div>
                  <Label className="text-muted-foreground">Taxa na Liquidação</Label>
                  <p className="font-medium">R$ {parseFloat(hedge.settlementSpotRate).toFixed(4)}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Empresa */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Empresa
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-muted-foreground">Razão Social</Label>
              <p className="font-medium">{hedge.company?.razaoSocial || "-"}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">CNPJ</Label>
              <p className="font-medium">
                {hedge.company?.cnpj?.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") || "-"}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Invoice */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Invoice
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-muted-foreground">Número</Label>
                <p className="font-medium">{hedge.invoice?.invoiceNumber || "-"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Fornecedor</Label>
                <p className="font-medium">{hedge.invoice?.supplierName || "-"}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-muted-foreground">País</Label>
                <p className="font-medium">{hedge.invoice?.supplierCountry || "-"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Valor Original</Label>
                <p className="font-medium">{formatUsdc(hedge.invoice?.amountUsdc || "0")}</p>
              </div>
            </div>
            {hedge.invoice?.description && (
              <div>
                <Label className="text-muted-foreground">Descrição</Label>
                <p className="font-medium">{hedge.invoice.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Datas e Timeline */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
              <div>
                <Label className="text-muted-foreground">Criação</Label>
                <p className="font-medium">{formatDate(hedge.createdAt)}</p>
              </div>
              {hedge.guaranteeDepositedAt && (
                <div>
                  <Label className="text-muted-foreground">Garantia Depositada</Label>
                  <p className="font-medium">{formatDate(hedge.guaranteeDepositedAt)}</p>
                </div>
              )}
              {hedge.approvedAt && (
                <div>
                  <Label className="text-muted-foreground">Aprovação</Label>
                  <p className="font-medium">{formatDate(hedge.approvedAt)}</p>
                </div>
              )}
              <div>
                <Label className="text-muted-foreground">Vencimento</Label>
                <p className="font-medium">{formatDate(hedge.dueDate)}</p>
              </div>
              {hedge.gracePeriodEndDate && (
                <div>
                  <Label className="text-muted-foreground">Fim Carência</Label>
                  <p className="font-medium">{formatDate(hedge.gracePeriodEndDate)}</p>
                </div>
              )}
              {hedge.settledAt && (
                <div>
                  <Label className="text-muted-foreground">Liquidação</Label>
                  <p className="font-medium">{formatDate(hedge.settledAt)}</p>
                </div>
              )}
            </div>

            {/* Notes */}
            {(hedge.notes || hedge.rejectionReason) && (
              <div className="mt-6 pt-6 border-t">
                {hedge.rejectionReason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <Label className="text-red-700">Motivo da Rejeição</Label>
                    <p className="text-red-900 mt-1">{hedge.rejectionReason}</p>
                  </div>
                )}
                {hedge.notes && (
                  <div className="bg-muted/50 rounded-lg p-4 mt-4">
                    <Label className="text-muted-foreground">Observações</Label>
                    <p className="mt-1">{hedge.notes}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Termo de Proteção */}
        {hedge.termAcceptedAt && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="h-5 w-5 text-green-600" />
                Termo de Proteção Cambial
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span className="font-medium">Termo aceito pela empresa</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Aceito em {formatDateTime(hedge.termAcceptedAt)}
                  </p>
                  {hedge.termHash && (
                    <p className="text-xs text-muted-foreground font-mono break-all">
                      Hash SHA-256: {hedge.termHash}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={handleDownloadTerm}
                  disabled={downloadingTerm}
                >
                  {downloadingTerm ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Baixando...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Baixar Termo PDF
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aprovar Hedge</DialogTitle>
            <DialogDescription>
              Confirma a aprovação do hedge {hedge.hedgeCode}?
              A proteção será ativada e a empresa poderá usar a taxa travada na liquidação.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="approveNotes">Observações (opcional)</Label>
              <Textarea
                id="approveNotes"
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                placeholder="Adicione observações se necessário..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleApprove} disabled={actionLoading}>
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar Aprovação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar Hedge</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja rejeitar o hedge {hedge.hedgeCode}?
              A garantia será devolvida à empresa.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="rejectReason">Motivo da Rejeição *</Label>
              <Textarea
                id="rejectReason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Informe o motivo da rejeição..."
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={actionLoading || !rejectReason}>
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar Rejeição
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Default Dialog */}
      <Dialog open={showDefaultDialog} onOpenChange={setShowDefaultDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marcar como Inadimplente</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja marcar o hedge {hedge.hedgeCode} como inadimplente?
              Esta ação indica que a empresa não cumpriu com a liquidação no prazo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="defaultNotes">Observações (opcional)</Label>
              <Textarea
                id="defaultNotes"
                value={defaultNotes}
                onChange={(e) => setDefaultNotes(e.target.value)}
                placeholder="Adicione observações se necessário..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDefaultDialog(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleMarkDefaulted} disabled={actionLoading}>
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar Inadimplência
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
